import { useAtom } from "jotai";
import { useEffect } from "react";
import { useMerchflowStoresApi } from "src/api/merchflows/stores";
import { useLoaders } from "src/components/InlineLoader";
import { useModals } from "src/components/Modals";
import { useToasts } from "src/components/Toasts";
import { Button, Color, Flex, Icon, Modal, Text } from "src/elements";
import { RouteDeleteMerchflows_MerchflowId_StoresPogs } from "@CommonApi/merchflows/_merchflowId_/stores/pogs/delete";
import { deleteStorePogModalAtom } from "./store/atoms";
import { getDeleteBlockedState, getPogDeleteLabel } from "./store/blockUtils";

export const ModalDeleteStorePogs = () => {
  const [data, setData] = useAtom(deleteStorePogModalAtom);

  useEffect(() => {
    return () => {
      setData(null);
    };
  }, []);

  if (!data) {
    return null;
  }

  return <ModalDeleteStorePogsInner {...data} />;
};

interface Props {
  storePogIds: number[];
  approvedCount: number;
  inProgressCount: number;
  isLastPog: boolean;
  merchflowId: number;
  onSuccess: () => void;
}

const ModalDeleteStorePogsInner = ({
  storePogIds,
  approvedCount,
  inProgressCount,
  isLastPog,
  merchflowId,
  onSuccess,
}: Props) => {
  const { closeModal: _closeModal } = useModals();
  const { hasLoader } = useLoaders();
  const { toast } = useToasts();
  const { deleteMerchflowStorePogsApi } = useMerchflowStoresApi();

  const isLoading = hasLoader(RouteDeleteMerchflows_MerchflowId_StoresPogs);
  const eligibleCount = storePogIds.length - inProgressCount;
  const blockedState = getDeleteBlockedState(isLastPog, approvedCount, eligibleCount);

  const closeModal = () => {
    _closeModal("ModalDeleteStorePogs");
  };

  const handleDelete = async () => {
    const result = await deleteMerchflowStorePogsApi(merchflowId, storePogIds);

    if (result) {
      onSuccess();
      closeModal();

      if (result.skipped_count > 0) {
        toast({ title: `${result.skipped_count} in-progress planogram(s) were skipped.` });
      }
    }
  };

  if (blockedState) {
    const { title: blockedTitle, body: blockedBody } = blockedState;

    return (
      <Modal name="ModalDeleteStorePogs" width="400px" isConfirmModal>
        <Flex column padding="20px 10px 10px 10px" gap="30px" justify="center">
          <Flex column justify="center" gap="10px">
            <Flex
              width="56px"
              height="56px"
              align="center"
              justify="center"
              borderRadius="50%"
              background={Color.dangerDisabled}
            >
              <Icon name="alert" color={Color.danger} size={28} />
            </Flex>

            <Text variant="h4" textAlign="center">
              {blockedTitle}
            </Text>

            <Text variant="body3" color={Color.textSecondary} textAlign="center">
              {blockedBody}
            </Text>
          </Flex>

          <Button width="100%" color="primary" variant="inverted" onClick={closeModal}>
            Close
          </Button>
        </Flex>
      </Modal>
    );
  }

  const pogLabel = getPogDeleteLabel(eligibleCount);

  return (
    <Modal name="ModalDeleteStorePogs" width="400px" isConfirmModal>
      <Flex column padding="20px 10px 10px 10px" gap="30px" justify="center">
        <Flex column justify="center" gap="10px">
          <Flex
            width="56px"
            height="56px"
            align="center"
            justify="center"
            borderRadius="50%"
            background={Color.dangerDisabled}
          >
            <Icon name="alert" color={Color.danger} size={28} />
          </Flex>

          <Text variant="h4" textAlign="center">
            {`Send ${eligibleCount} ${pogLabel} to trash?`}
          </Text>

          <Text variant="body3" color={Color.textSecondary} textAlign="center">
            Items in the trash can only be restored within 90 days.
          </Text>

          {inProgressCount > 0 && (
            <Text variant="body3" color={Color.textSecondary} textAlign="center">
              {`${inProgressCount} in-progress ${getPogDeleteLabel(inProgressCount)} will be skipped.`}
            </Text>
          )}
        </Flex>

        <Flex width="100%" gap="10px">
          <Button width="100%" color="primary" variant="inverted" onClick={closeModal}>
            Cancel
          </Button>

          <Button width="100%" color="red" isLoading={isLoading} onClick={handleDelete}>
            Delete
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};
